import { Download, FileArchive, LoaderCircle, RefreshCw, ScanSearch, Search, Sparkles, Upload } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, downloadFile } from "../api";
import { EmptyState, LoadingState, MetricStrip, PageHeader, Pagination, SectionHeading, formatDate } from "../components";
import { useAppData } from "../context";
import type { ImportJob, ProductMapping, ProductReadinessItem, ProductReadinessRun } from "../types";

const PAGE_SIZE = 30;
const READINESS_PAGE_SIZE = 20;

interface MappingImportResult {
  accountCode: string;
  rowCount: number;
  mappingCount: number;
  skuCount: number;
  siteCount: number;
  rejectedRows: number;
  accountLabels: string[];
  syncedAt: string;
}

function readinessStatus(status: ProductReadinessRun["status"]) {
  if (status === "RUNNING") return "检查中";
  if (status === "SUCCESS") return "全部完成";
  if (status === "PARTIAL") return "部分完成";
  return "失败";
}

function capability(issues: string[], errorCode: string | null) {
  if (errorCode) return <span className="capability mock_only" title={errorCode}>读取失败</span>;
  if (issues.length) return <span className="capability mock_only" title={issues.join("、")}>受限</span>;
  return <span className="capability verified">可修改</span>;
}

export function ProductMappings() {
  const { data } = useAppData();
  const [items, setItems] = useState<ProductMapping[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [accountCode, setAccountCode] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [importJob, setImportJob] = useState<ImportJob<MappingImportResult> | null>(null);
  const [importJobId, setImportJobId] = useState(() => window.localStorage.getItem("meliforge-mapping-import"));
  const [readinessRuns, setReadinessRuns] = useState<ProductReadinessRun[]>([]);
  const [readinessRun, setReadinessRun] = useState<ProductReadinessRun | null>(null);
  const [readinessItems, setReadinessItems] = useState<ProductReadinessItem[]>([]);
  const [readinessTotal, setReadinessTotal] = useState(0);
  const [readinessPage, setReadinessPage] = useState(1);
  const [readinessCapability, setReadinessCapability] = useState("");
  const [adjustmentPercent, setAdjustmentPercent] = useState("0");
  const [readinessBusy, setReadinessBusy] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!accountCode && data?.accounts[0]) setAccountCode(data.accounts[0].accountCode);
  }, [accountCode, data]);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (accountCode) params.set("accountCode", accountCode);
    if (status) params.set("status", status);
    if (query) params.set("search", query);
    setLoading(true);
    try {
      const result = await api.productMappings(params);
      setItems(result.items);
      setTotal(result.total);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "商品映射加载失败。" );
    } finally {
      setLoading(false);
    }
  }, [accountCode, page, query, status]);

  useEffect(() => { void load(); }, [load]);

  const loadReadinessItems = useCallback(async (runId: string, targetPage = readinessPage, targetCapability = readinessCapability) => {
    const params = new URLSearchParams({ page: String(targetPage), pageSize: String(READINESS_PAGE_SIZE) });
    if (targetCapability) params.set("capability", targetCapability);
    const result = await api.productReadinessItems(runId, params);
    setReadinessItems(result.items);
    setReadinessTotal(result.total);
  }, [readinessCapability, readinessPage]);

  useEffect(() => {
    if (!accountCode) {
      setReadinessRuns([]); setReadinessRun(null); setReadinessItems([]); setReadinessTotal(0);
      return;
    }
    let stopped = false;
    api.productReadinessRuns(accountCode).then((result) => {
      if (stopped) return;
      setReadinessRuns(result.items);
      setReadinessRun((current) => result.items.find((run) => run.id === current?.id) ?? result.items[0] ?? null);
    }).catch((error) => { if (!stopped) setMessage(error instanceof Error ? error.message : "全量检查记录加载失败。"); });
    return () => { stopped = true; };
  }, [accountCode]);

  useEffect(() => {
    if (!readinessRun) { setReadinessItems([]); setReadinessTotal(0); return; }
    void loadReadinessItems(readinessRun.id).catch((error) => setMessage(error instanceof Error ? error.message : "商品能力结果加载失败。"));
  }, [loadReadinessItems, readinessRun?.id]);

  useEffect(() => {
    if (!readinessRun || readinessRun.status !== "RUNNING") return;
    let stopped = false;
    const poll = async () => {
      try {
        const next = await api.productReadinessRun(readinessRun.id);
        if (stopped) return;
        setReadinessRun(next);
        await loadReadinessItems(next.id);
        if (next.status !== "RUNNING") {
          const history = await api.productReadinessRuns(next.accountCode);
          if (!stopped) {
            setReadinessRuns(history.items);
            setMessage(`全量检查完成：成功回读 ${next.successCount.toLocaleString()} 条，失败 ${next.failedCount.toLocaleString()} 条。`);
          }
        }
      } catch (error) {
        if (!stopped) setMessage(error instanceof Error ? error.message : "全量检查进度读取失败。");
      }
    };
    const timer = window.setInterval(() => void poll(), 1200);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [loadReadinessItems, readinessRun?.id, readinessRun?.status]);

  useEffect(() => {
    if (!importJobId) return;
    let stopped = false;
    async function poll() {
      try {
        const job = await api.importJob<MappingImportResult>(importJobId!);
        if (stopped) return;
        setImportJob(job);
        if (job.status === "COMPLETED" && job.result) {
          const result = job.result;
          setMessage(`Listing 导入完成：读取 ${result.rowCount.toLocaleString()} 行，建立 ${result.mappingCount.toLocaleString()} 条映射，覆盖 ${result.siteCount} 个站点；跳过 ${result.rejectedRows} 行。`);
          window.localStorage.removeItem("meliforge-mapping-import");
          setImportJobId(null);
          setPage(1);
          await load();
        } else if (job.status === "FAILED") {
          setMessage(job.errorMessage || "Listing 映射导入失败。" );
          window.localStorage.removeItem("meliforge-mapping-import");
          setImportJobId(null);
        }
      } catch (error) {
        if (!stopped) setMessage(error instanceof Error ? error.message : "映射导入进度读取失败。" );
      }
    }
    void poll();
    const timer = window.setInterval(() => void poll(), 900);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [importJobId, load]);

  async function sync() {
    if (!accountCode) return;
    setBusy(true); setMessage("");
    try {
      const result = await api.syncProductMappings(accountCode);
      setMessage(`官方同步完成：读取 ${result.itemCount.toLocaleString()} 个父商品，确认 ${result.publishedParentCount.toLocaleString()} 个父商品、${result.listingCount.toLocaleString()} 条真实站点 Listing；${result.unpublishedParentCount.toLocaleString()} 个父商品尚未发布到授权站点，${result.unmappedItemCount.toLocaleString()} 个商品未提供商家 SKU。`);
      setPage(1);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "商品映射同步失败。" );
    } finally {
      setBusy(false);
    }
  }

  async function importListing(file: File) {
    if (!accountCode) return;
    setBusy(true); setMessage("");
    try {
      const job = await api.importProductMappings(accountCode, file);
      setImportJob(job);
      setImportJobId(job.id);
      window.localStorage.setItem("meliforge-mapping-import", job.id);
      setMessage(`${file.name} 已保存，正在后台建立 SKU 映射。`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Listing 映射导入失败。" );
    } finally {
      setBusy(false);
    }
  }

  async function startReadiness() {
    if (!accountCode) return;
    setReadinessBusy("scan"); setMessage("");
    try {
      const run = await api.startProductReadiness(accountCode);
      setReadinessRun(run);
      setReadinessRuns((current) => [run, ...current.filter((item) => item.id !== run.id)]);
      setReadinessPage(1); setReadinessCapability("");
      setMessage("已开始通过 Mercado Libre 官方 API 回读全部真实站点商品；该过程不会修改商品。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "全量检查启动失败。");
    } finally { setReadinessBusy(""); }
  }

  async function exportReadiness(kind: "ai" | "pricing") {
    if (!readinessRun) return;
    setReadinessBusy(kind); setMessage("");
    try {
      if (kind === "ai") {
        await downloadFile(`/api/product-readiness/runs/${encodeURIComponent(readinessRun.id)}/ai-source`, `meliforge-source-supplement-${readinessRun.id}.xlsx`);
      } else {
        const percent = Number(adjustmentPercent);
        if (!Number.isFinite(percent) || percent < -90 || percent > 500) throw new Error("调价比例必须在 -90% 到 500% 之间。");
        await downloadFile(`/api/product-readiness/runs/${encodeURIComponent(readinessRun.id)}/net-proceeds?percent=${encodeURIComponent(percent)}`, `meliforge-net-proceeds-${readinessRun.id}.xlsx`);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "批量文件生成失败。");
    } finally { setReadinessBusy(""); }
  }

  if (!data) return <LoadingState />;
  const ambiguous = items.filter((item) => item.mappingStatus === "AMBIGUOUS").length;
  const readinessProgress = readinessRun?.totalCount ? Math.round(readinessRun.processedCount / readinessRun.totalCount * 100) : 0;
  const readinessFinished = Boolean(readinessRun && readinessRun.status !== "RUNNING" && readinessRun.status !== "FAILED");
  return <>
    <PageHeader eyebrow="CATALOG / SKU MAPPING" title="商品映射" actions={<>
      <input ref={fileInputRef} type="file" hidden accept=".csv,.zip,text/csv,application/zip" onChange={(event) => { const file = event.target.files?.[0]; if (file) void importListing(file); event.currentTarget.value = ""; }} />
      <button className="button secondary" disabled={busy || Boolean(importJobId) || !accountCode} onClick={() => fileInputRef.current?.click()}>{importJobId ? <LoaderCircle size={17} className="spin" /> : <Upload size={17} />}{importJobId ? `导入中 ${importJob?.progress ?? 0}%` : "导入 Listing"}</button>
      <button className="button primary" disabled={busy || Boolean(importJobId) || !accountCode} onClick={() => void sync()}>{busy ? <LoaderCircle size={17} className="spin" /> : <RefreshCw size={17} />}同步当前账号</button>
    </>} />
    {message ? <div className="inline-message" role="status">{message}</div> : null}
    <MetricStrip items={[
      { label: "映射记录", value: total.toLocaleString() },
      { label: "当前页唯一", value: items.length - ambiguous, tone: "positive" },
      { label: "当前页冲突", value: ambiguous, tone: ambiguous ? "warning" : "" },
      { label: "账号", value: accountCode || "--" },
    ]} />
    <section className="workspace-band readiness-band">
      <SectionHeading title="批量维护准备" meta="官方 API 实时回读" action={readinessRun ? <span className={`status-badge ${readinessRun.status === "SUCCESS" ? "positive" : readinessRun.status === "RUNNING" ? "active" : "warning"}`}><span />{readinessStatus(readinessRun.status)}</span> : undefined} />
      <div className="readiness-toolbar">
        <button className="button primary" disabled={!accountCode || readinessRun?.status === "RUNNING" || readinessBusy === "scan"} onClick={() => void startReadiness()}>{readinessBusy === "scan" || readinessRun?.status === "RUNNING" ? <LoaderCircle size={17} className="spin" /> : <ScanSearch size={17} />}检查全部真实商品</button>
        <button className="button secondary" disabled={!readinessFinished || Boolean(readinessBusy)} onClick={() => void exportReadiness("ai")}>{readinessBusy === "ai" ? <LoaderCircle size={17} className="spin" /> : <Download size={17} />}资料补充模板</button>
        <div className="readiness-adjustment"><label htmlFor="readiness-percent">调价比例</label><input id="readiness-percent" type="number" min="-90" max="500" step="0.1" value={adjustmentPercent} onChange={(event) => setAdjustmentPercent(event.target.value)} /><span>%</span><button className="button secondary" disabled={!readinessFinished || Boolean(readinessBusy)} onClick={() => void exportReadiness("pricing")}>{readinessBusy === "pricing" ? <LoaderCircle size={17} className="spin" /> : <Download size={17} />}Net Proceeds 表</button></div>
        <Link className="button secondary" to="/ai-content"><Sparkles size={17} />进入 AI 人工审核</Link>
      </div>
      {readinessRun ? <>
        <div className="readiness-progress" aria-label={`检查进度 ${readinessProgress}%`}><div><span style={{ width: `${readinessProgress}%` }} /></div><strong>{readinessRun.processedCount.toLocaleString()} / {readinessRun.totalCount.toLocaleString()}</strong><small>{readinessProgress}%</small></div>
        <div className="readiness-summary">
          <div><span>成功回读</span><strong>{readinessRun.successCount.toLocaleString()}</strong></div>
          <div><span>标题可改</span><strong>{readinessRun.titleEditableCount.toLocaleString()}</strong></div>
          <div><span>描述可改</span><strong>{readinessRun.descriptionEditableCount.toLocaleString()}</strong></div>
          <div><span>收益可调</span><strong>{readinessRun.pricingEditableCount.toLocaleString()}</strong></div>
          <div><span>读取失败</span><strong className={readinessRun.failedCount ? "count-negative" : ""}>{readinessRun.failedCount.toLocaleString()}</strong></div>
        </div>
        <div className="readiness-result-head">
          <select aria-label="检查批次" value={readinessRun.id} onChange={(event) => { const next = readinessRuns.find((run) => run.id === event.target.value); if (next) { setReadinessPage(1); setReadinessRun(next); } }}>{readinessRuns.map((run) => <option key={run.id} value={run.id}>{formatDate(run.startedAt)} · {readinessStatus(run.status)}</option>)}</select>
          <select aria-label="修改能力" value={readinessCapability} onChange={(event) => { setReadinessPage(1); setReadinessCapability(event.target.value); }}><option value="">全部结果</option><option value="TITLE">标题可修改</option><option value="DESCRIPTION">描述可修改</option><option value="PRICING">Net Proceeds 可调整</option><option value="FAILED">读取失败</option></select>
          <span>{readinessTotal.toLocaleString()} 条</span>
        </div>
        {readinessItems.length ? <><div className="table-scroll"><table className="data-table readiness-table"><thead><tr><th>站点 / SKU</th><th>父商品 / 站点商品</th><th>当前标题</th><th>标题</th><th>描述</th><th>Net Proceeds</th><th>商品状态</th></tr></thead><tbody>{readinessItems.map((item) => <tr key={`${item.runId}:${item.parentItemId}:${item.siteCode}`}><td><strong>{item.siteCode}</strong><small>{item.merchantSku || "无 SKU"}</small></td><td className="mono"><strong>{item.parentItemId}</strong><small>{item.childItemId || "--"}</small></td><td title={item.title}>{item.title || item.errorMessage || "--"}</td><td>{capability(item.titleIssues, item.errorCode)}</td><td>{capability(item.descriptionIssues, item.errorCode)}</td><td>{capability(item.pricingIssues, item.errorCode)}{item.targetNetProceeds !== null ? <small>{item.currencyId} {item.targetNetProceeds.toFixed(2)}</small> : null}</td><td>{item.listingStatus || item.parentStatus || item.errorCode || "--"}</td></tr>)}</tbody></table></div><Pagination page={readinessPage} total={readinessTotal} pageSize={READINESS_PAGE_SIZE} onChange={setReadinessPage} /></> : <EmptyState title={readinessRun.status === "RUNNING" ? "正在回读商品" : "当前筛选条件下没有结果"} />}
      </> : <EmptyState title="尚未检查商品修改能力" />}
    </section>
    <section className="workspace-band">
      <SectionHeading title="SKU 与平台商品" meta={`${total.toLocaleString()} 条记录`} action={<span className="muted-text"><FileArchive size={15} />支持 CSV 或 CSV.ZIP</span>} />
      <div className="filter-bar">
        <label className="search-field"><Search size={16} /><input aria-label="搜索 SKU 或商品" value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { setPage(1); setQuery(search.trim()); } }} placeholder="SKU、Item ID、标题" /></label>
        <select aria-label="账号" value={accountCode} onChange={(event) => { setPage(1); setAccountCode(event.target.value); }}><option value="">全部账号</option>{data.accounts.map((account) => <option key={account.id} value={account.accountCode}>{account.accountCode}</option>)}</select>
        <select aria-label="映射状态" value={status} onChange={(event) => { setPage(1); setStatus(event.target.value); }}><option value="">全部状态</option><option value="ACTIVE">唯一映射</option><option value="AMBIGUOUS">映射冲突</option></select>
        <button className="button secondary" onClick={() => { setPage(1); setQuery(search.trim()); }}>查询</button>
      </div>
      {loading ? <LoadingState /> : items.length ? <><div className="table-scroll"><table className="data-table mapping-table"><thead><tr><th>账号 / 站点</th><th>商家 SKU</th><th>Item ID</th><th>商品标题</th><th>商品状态</th><th>映射状态</th><th>同步时间</th></tr></thead><tbody>{items.map((item) => <tr key={`${item.accountCode}:${item.siteCode}:${item.merchantSku}:${item.itemId}`}><td><strong>{item.accountCode}</strong><small>{item.siteCode}</small></td><td className="mono"><strong>{item.merchantSku}</strong></td><td className="mono">{item.itemId}</td><td>{item.title || "--"}</td><td>{item.itemStatus || "--"}</td><td><span className={`capability ${item.mappingStatus === "ACTIVE" ? "verified" : "mock_only"}`}>{item.mappingStatus === "ACTIVE" ? "唯一" : `${item.matchCount} 个匹配`}</span></td><td>{formatDate(item.syncedAt)}</td></tr>)}</tbody></table></div><Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} /></> : <EmptyState title="当前筛选条件下没有商品映射" />}
    </section>
  </>;
}
